import { Ref } from 'react';
import ReactBaseTable, { ColumnShape, BaseTableProps as ReactBaseTableProps } from 'react-base-table';
import { isFunction, isPlainObject } from 'lodash';

import './BaseTable.scss';
import { formatCopiedAsTable, TABLE_PART_TYPE_ATTRIBUTE, TABLE_PARTS } from './BaseTable.utils';

export type BaseTableProps<T> = ReactBaseTableProps<T> & {
  forwardedRef?: Ref<ReactBaseTable<T>>;
  // `BaseTableProps` can be extended here
};


const expandTableProperty = (
  property: Function | object = {},
  args: object,
  expander: { [TABLE_PART_TYPE_ATTRIBUTE]: TABLE_PARTS },
): object => {
  if (isFunction(property)) {
    return {
      ...property(args),
      ...expander,
    };
  }

  if (isPlainObject(property)) {
    return {
      ...property,
      ...expander,
    };
  }

  return expander;
};


function BaseTable<T>({ headerHeight = 38, forwardedRef, ...rest }: BaseTableProps<T>): JSX.Element {
  const { rowProps, cellProps } = rest;

  const updateRowProps = (rowPropsArgs: {
    columns: ColumnShape<T>[];
    rowData: T;
    rowIndex: number;
  }): object => expandTableProperty(rowProps, rowPropsArgs, {
    [TABLE_PART_TYPE_ATTRIBUTE]: TABLE_PARTS.ROW,
  });

  const updateCellProps = (cellPropsArgs: {
    columns: ColumnShape<T>[];
    column: ColumnShape<T>;
    columnIndex: number;
    rowData: T;
    rowIndex: number;
  }): object => expandTableProperty(cellProps, cellPropsArgs, {
    [TABLE_PART_TYPE_ATTRIBUTE]: TABLE_PARTS.CELL,
  });

  return (
    <ReactBaseTable
      headerHeight={headerHeight}
      ref={forwardedRef}
      rowEventHandlers={{
        onCopy: formatCopiedAsTable,
      }}
      {...rest}

      // Overriding the rowProps and cellProps from rest.
      rowProps={updateRowProps}
      cellProps={updateCellProps}
    />
  );
}

export default BaseTable;
